const MAX_TYPED_CHUNK_ERRORS = 20;

function nowIso() {
  return new Date().toISOString();
}

function cloneMessage(message) {
  return {
    ...message,
    parts: Array.isArray(message.parts)
      ? message.parts.map((part) => ({ ...part }))
      : [],
  };
}

// Clones the per-session stream cursor (pointer bookkeeping only). The message
// array is owned by the store and threaded into applyUiChunk separately, so the
// cursor no longer carries a copy of the messages.
function cloneCursor(previousCursor) {
  return {
    ...previousCursor,
    activeTextPartById: { ...(previousCursor.activeTextPartById || {}) },
    activeReasoningPartById: { ...(previousCursor.activeReasoningPartById || {}) },
    activeToolPartByCallId: { ...(previousCursor.activeToolPartByCallId || {}) },
    streamedMessageIds: Array.isArray(previousCursor.streamedMessageIds)
      ? [...previousCursor.streamedMessageIds]
      : [],
    errors: Array.isArray(previousCursor.errors) ? [...previousCursor.errors] : [],
    lastError: null,
  };
}

function appendError(cursor, code, message, chunk) {
  const error = {
    code,
    message,
    chunkType: chunk?.type || "unknown",
    at: nowIso(),
  };
  cursor.lastError = error;
  cursor.errors.push(error);
  if (cursor.errors.length > MAX_TYPED_CHUNK_ERRORS) {
    cursor.errors.shift();
  }
}

function getMessageIndexById(messages, messageId) {
  return messages.findIndex((msg) => msg.id === messageId);
}

// Copy-on-write: deep-clone messages[idx] the first time this pass touches it,
// tracking which indices we already own in `owned`. This keeps applyUiChunk
// O(1) in conversation length — only the streamed turn is cloned, not the whole
// persisted history — while never mutating the caller's (store-owned) messages.
function ensureOwned(messages, idx, owned) {
  if (idx < 0 || owned.has(idx)) return;
  messages[idx] = cloneMessage(messages[idx]);
  owned.add(idx);
}

// Returns the id of the last message only if it is still an in-progress
// assistant turn. Lets a `start` chunk that arrives without a messageId continue
// the open turn instead of minting a synthetic id that could never reconcile
// with the server-persisted message.
function lastStreamingMessageId(messages) {
  const last = messages[messages.length - 1];
  return last && last.role === "assistant" && last.status === "streaming"
    ? last.id
    : null;
}

function ensureMessage(messages, messageId, owned) {
  let messageIndex = getMessageIndexById(messages, messageId);
  if (messageIndex === -1) {
    messages.push({
      id: messageId,
      role: "assistant",
      status: "streaming",
      parts: [],
      createdAt: nowIso(),
      updatedAt: nowIso(),
    });
    messageIndex = messages.length - 1;
    owned.add(messageIndex);
  }
  return messageIndex;
}

function appendPart(messages, messageId, part, owned) {
  const messageIndex = ensureMessage(messages, messageId, owned);
  ensureOwned(messages, messageIndex, owned);
  const message = messages[messageIndex];
  message.parts.push(part);
  message.updatedAt = nowIso();
  return {
    messageIndex,
    partIndex: message.parts.length - 1,
  };
}

// Appends `part` only if no existing part of the turn already matches `isMatch`,
// otherwise replaces the matched part in place. Validation and step-start parts
// have no streamed delta lifecycle to reconcile them by id (unlike text /
// reasoning / tool), so a turn can receive them twice — once via the server's
// message_added (which writes the full persisted part list straight into the
// store, bypassing this reducer) and once via the live chunk processed here.
// Keying the append on the part's stable identity (validation: attempt;
// step-start: id) makes it idempotent and prevents the transient duplicate
// (e.g. two validation cards) before the authoritative server copy lands.
function upsertPartByIdentity(messages, messageId, part, isMatch, owned) {
  const messageIndex = ensureMessage(messages, messageId, owned);
  ensureOwned(messages, messageIndex, owned);
  const message = messages[messageIndex];
  const existingIndex = message.parts.findIndex(isMatch);
  if (existingIndex >= 0) {
    message.parts[existingIndex] = part;
  } else {
    message.parts.push(part);
  }
  message.updatedAt = nowIso();
}

function updatePart(messages, messageId, partIndex, updater, owned) {
  const messageIndex = getMessageIndexById(messages, messageId);
  if (messageIndex < 0) return false;
  ensureOwned(messages, messageIndex, owned);
  const message = messages[messageIndex];
  if (!Array.isArray(message.parts) || partIndex < 0 || partIndex >= message.parts.length) {
    return false;
  }
  const previousPart = message.parts[partIndex];
  message.parts[partIndex] = updater(previousPart);
  message.updatedAt = nowIso();
  return true;
}

// The streamed text and reasoning lifecycles are identical except for the
// cursor pointer-map they track and the part shape, so both route through the
// shared start/delta/end/finalize helpers below, parameterized by this
// descriptor. Adding a new streamed part kind is a new descriptor, not a new
// copy of four near-identical cases.
const TEXT_PART = {
  label: "text",
  mapKey: "activeTextPartById",
  makePart: (chunk) => ({ type: "text", id: chunk.id, text: "", state: "streaming" }),
  finalize: (part) => ({ ...part, state: "done" }),
  codes: {
    startWithoutStart: "TEXT_START_WITHOUT_START",
    deltaWithoutStart: "TEXT_DELTA_WITHOUT_TEXT_START",
    endWithoutStart: "TEXT_END_WITHOUT_TEXT_START",
    pointerInvalid: "TEXT_POINTER_INVALID",
    finalizeMissing: "MISSING_ACTIVE_TEXT_PART",
  },
};

const REASONING_PART = {
  label: "reasoning",
  mapKey: "activeReasoningPartById",
  makePart: (chunk) => ({
    type: "reasoning",
    id: chunk.id,
    text: "",
    state: "streaming",
    startedAt: nowIso(),
  }),
  finalize: (part) => ({ ...part, state: "done", endedAt: nowIso() }),
  codes: {
    startWithoutStart: "REASONING_START_WITHOUT_START",
    deltaWithoutStart: "REASONING_DELTA_WITHOUT_REASONING_START",
    endWithoutStart: "REASONING_END_WITHOUT_REASONING_START",
    pointerInvalid: "REASONING_POINTER_INVALID",
    finalizeMissing: "MISSING_ACTIVE_REASONING_PART",
  },
};

function handlePartStart(messages, cursor, chunk, kind, owned) {
  if (!cursor.currentMessageId) {
    appendError(cursor, kind.codes.startWithoutStart, `received ${kind.label}-start before start`, chunk);
    return;
  }
  const pointer = appendPart(messages, cursor.currentMessageId, kind.makePart(chunk), owned);
  cursor[kind.mapKey][chunk.id] = {
    messageId: cursor.currentMessageId,
    partIndex: pointer.partIndex,
  };
}

function handlePartDelta(messages, cursor, chunk, kind, owned) {
  const pointer = cursor[kind.mapKey][chunk.id];
  if (!pointer) {
    appendError(cursor, kind.codes.deltaWithoutStart, `received ${kind.label}-delta for unknown ${kind.label} part ${chunk.id}`, chunk);
    return;
  }
  const updated = updatePart(messages, pointer.messageId, pointer.partIndex, (part) => ({
    ...part,
    text: `${part.text || ""}${chunk.delta || ""}`,
  }), owned);
  if (!updated) {
    appendError(cursor, kind.codes.pointerInvalid, `${kind.label} part pointer invalid for ${chunk.id}`, chunk);
  }
}

function handlePartEnd(messages, cursor, chunk, kind, owned) {
  const pointer = cursor[kind.mapKey][chunk.id];
  if (!pointer) {
    appendError(cursor, kind.codes.endWithoutStart, `received ${kind.label}-end for unknown ${kind.label} part ${chunk.id}`, chunk);
    return;
  }
  const updated = updatePart(messages, pointer.messageId, pointer.partIndex, kind.finalize, owned);
  if (!updated) {
    appendError(cursor, kind.codes.pointerInvalid, `${kind.label} part pointer invalid for ${chunk.id}`, chunk);
  }
  delete cursor[kind.mapKey][chunk.id];
}

// Finalizes any still-open parts of `kind` on stream finish (marks done, clears
// the pointer map).
function finalizeActiveParts(messages, cursor, kind, owned) {
  for (const [partId, pointer] of Object.entries(cursor[kind.mapKey] || {})) {
    const ok = updatePart(messages, pointer.messageId, pointer.partIndex, kind.finalize, owned);
    if (!ok) {
      appendError(cursor, kind.codes.finalizeMissing, `could not finalize active ${kind.label} part ${partId}`, { type: "finish" });
    }
  }
  cursor[kind.mapKey] = {};
}

function upsertToolPartPointer(messages, cursor, chunk, fallbackToolName, owned) {
  const existingPointer = cursor.activeToolPartByCallId[chunk.toolCallId];
  if (existingPointer) return existingPointer;

  // Orphan tool chunk (no open turn, e.g. replay after a reconnect-reset): drop
  // it. The server snapshot is authoritative for the persisted turn — minting a
  // synthetic id here is what produced duplicate cards in the rendered list.
  if (!cursor.currentMessageId) return null;
  const { partIndex } = appendPart(messages, cursor.currentMessageId, {
    type: "tool",
    toolCallId: chunk.toolCallId,
    toolName: chunk.toolName || fallbackToolName,
    state: "input-streaming",
    inputText: "",
    input: undefined,
    output: undefined,
    errorText: null,
  }, owned);
  const pointer = { messageId: cursor.currentMessageId, partIndex };
  cursor.activeToolPartByCallId[chunk.toolCallId] = pointer;
  return pointer;
}

// Resolves the tool part for a tool-* chunk and applies `updater` to it. Drops
// the chunk (recording an error) when there is no open turn to attach to, or
// when the resolved pointer no longer addresses a live part.
function applyToolPartUpdate(messages, cursor, chunk, fallbackToolName, updater, owned) {
  const pointer = upsertToolPartPointer(messages, cursor, chunk, fallbackToolName, owned);
  if (!pointer) {
    appendError(
      cursor,
      "TOOL_CHUNK_WITHOUT_MESSAGE",
      `received ${chunk.type} for ${chunk.toolCallId} with no active assistant message`,
      chunk
    );
    return;
  }
  const updated = updatePart(messages, pointer.messageId, pointer.partIndex, updater, owned);
  if (!updated) {
    appendError(
      cursor,
      "TOOL_POINTER_INVALID",
      `tool pointer invalid for ${chunk.toolCallId}`,
      chunk
    );
  }
}

// Creates a fresh per-session stream cursor. Pointer bookkeeping only — the
// message array lives in the store and is passed into applyUiChunk.
export function createUiChunkSessionState(chatSessionId) {
  return {
    chatSessionId: String(chatSessionId || ""),
    streamVersion: "v2",
    currentMessageId: null,
    // Ids of the assistant turns this stream has opened since the last finish.
    // The store flushes all of them on finish (a multi-iteration run can finish
    // several turns at once), then the reducer clears the list.
    streamedMessageIds: [],
    activeTextPartById: {},
    activeReasoningPartById: {},
    activeToolPartByCallId: {},
    chunkCount: 0,
    finished: false,
    finishReason: null,
    lastChunkAt: null,
    lastChunkType: null,
    errors: [],
    lastError: null,
  };
}

// Applies one UI chunk against the caller's message array. The cursor carries
// pointer bookkeeping only; the messages array is owned by the store. Returns a
// new cursor and a new (clone-on-write) messages array — neither input is
// mutated. The reducer only ever appends/updates the assistant turn addressed
// by `cursor.currentMessageId`; user and persisted messages pass through
// untouched.
export function applyUiChunk(previousCursor, chunk, messages) {
  const cursor = cloneCursor(previousCursor || createUiChunkSessionState(""));
  // Shallow array copy; `owned` tracks which indices we deep-clone before
  // mutating (copy-on-write), so untouched persisted messages are never cloned.
  const nextMessages = Array.isArray(messages) ? messages.slice() : [];
  const owned = new Set();
  cursor.chunkCount = Number(cursor.chunkCount || 0) + 1;
  cursor.lastChunkAt = nowIso();
  cursor.lastChunkType = chunk?.type || "unknown";

  if (!chunk || typeof chunk !== "object") {
    appendError(cursor, "INVALID_CHUNK", "chunk must be an object", chunk);
    return { cursor, messages: nextMessages };
  }

  switch (chunk.type) {
    case "start": {
      if (cursor.finished) {
        cursor.activeTextPartById = {};
        cursor.activeReasoningPartById = {};
        cursor.activeToolPartByCallId = {};
      }
      // Prefer the server's messageId (the persisted iterationMessageId), else
      // continue the open streaming turn. The synthetic id is a last resort
      // only: it cannot match a persisted message, so the store can no longer
      // reconcile the live turn by id and a duplicate turn would render (the
      // double-cards regression). The backend always sends a messageId on
      // `start` (UiChunkEmitter.emitStart), so this branch should be unreachable.
      const messageId =
        typeof chunk.messageId === "string" && chunk.messageId.trim()
          ? chunk.messageId.trim()
          : lastStreamingMessageId(nextMessages) || `assistant-${Date.now()}-${cursor.chunkCount}`;
      ensureMessage(nextMessages, messageId, owned);
      cursor.currentMessageId = messageId;
      if (!cursor.streamedMessageIds.includes(messageId)) {
        cursor.streamedMessageIds.push(messageId);
      }
      cursor.finished = false;
      cursor.finishReason = null;
      break;
    }

    case "step-start": {
      // Boundary between LLM iterations within one turn. Appended as a part to the
      // already-open turn message (one message per turn) so the renderer can draw a
      // step divider; it never opens a new message.
      if (!cursor.currentMessageId) {
        appendError(cursor, "STEP_START_WITHOUT_START", "received step-start before start", chunk);
        break;
      }
      // Idempotent on the step id: the same step-start can arrive via the
      // server message_added and the live chunk; dedupe so the turn does not
      // accumulate duplicate boundary markers.
      upsertPartByIdentity(
        nextMessages,
        cursor.currentMessageId,
        { type: "step-start", id: chunk.id, state: "done" },
        (p) => p && p.type === "step-start" && chunk.id != null && p.id === chunk.id,
        owned
      );
      break;
    }

    case "text-start":
      handlePartStart(nextMessages, cursor, chunk, TEXT_PART, owned);
      break;

    case "text-delta":
      handlePartDelta(nextMessages, cursor, chunk, TEXT_PART, owned);
      break;

    case "text-end":
      handlePartEnd(nextMessages, cursor, chunk, TEXT_PART, owned);
      break;

    case "reasoning-start":
      handlePartStart(nextMessages, cursor, chunk, REASONING_PART, owned);
      break;

    case "reasoning-delta":
      handlePartDelta(nextMessages, cursor, chunk, REASONING_PART, owned);
      break;

    case "reasoning-end":
      handlePartEnd(nextMessages, cursor, chunk, REASONING_PART, owned);
      break;

    case "tool-input-start": {
      if (!cursor.currentMessageId) {
        appendError(
          cursor,
          "TOOL_INPUT_START_WITHOUT_START",
          "received tool-input-start before start",
          chunk
        );
        break;
      }
      const existingToolPointer = cursor.activeToolPartByCallId[chunk.toolCallId];
      if (existingToolPointer) {
        updatePart(nextMessages, existingToolPointer.messageId, existingToolPointer.partIndex, (part) => ({
          ...part,
          toolName: chunk.toolName || part.toolName,
        }), owned);
        break;
      }
      const pointer = appendPart(nextMessages, cursor.currentMessageId, {
        type: "tool",
        toolCallId: chunk.toolCallId,
        toolName: chunk.toolName,
        state: "input-streaming",
        inputText: "",
        input: undefined,
        output: undefined,
        errorText: null,
      }, owned);
      cursor.activeToolPartByCallId[chunk.toolCallId] = {
        messageId: cursor.currentMessageId,
        partIndex: pointer.partIndex,
      };
      break;
    }

    case "tool-input-delta": {
      applyToolPartUpdate(nextMessages, cursor, chunk, "unknown_tool", (part) => ({
        ...part,
        state: "input-streaming",
        inputText: `${part.inputText || ""}${chunk.inputTextDelta || ""}`,
      }), owned);
      break;
    }

    case "tool-input-available": {
      applyToolPartUpdate(nextMessages, cursor, chunk, chunk.toolName, (part) => ({
        ...part,
        toolName: chunk.toolName || part.toolName,
        state: "input-available",
        input: chunk.input,
      }), owned);
      break;
    }

    case "tool-output-available": {
      applyToolPartUpdate(nextMessages, cursor, chunk, "unknown_tool", (part) => ({
        ...part,
        state: "output-available",
        output: chunk.output,
        errorText: null,
      }), owned);
      break;
    }

    case "tool-output-error": {
      applyToolPartUpdate(nextMessages, cursor, chunk, "unknown_tool", (part) => ({
        ...part,
        state: "output-error",
        errorText: chunk.errorText,
      }), owned);
      break;
    }

    case "validation": {
      // Attaches the output-validation result to the turn it evaluated, in front
      // of the upcoming finish, so it renders inline rather than as a sibling row.
      if (!cursor.currentMessageId) {
        appendError(cursor, "VALIDATION_WITHOUT_START", "received validation before start", chunk);
        break;
      }
      // Idempotent on (attempt, validationType): the same evaluation can arrive
      // via the server message_added (full persisted part list written straight
      // to the store) and via this live chunk. Without dedupe the turn shows two
      // validation cards until the authoritative server copy replaces it.
      upsertPartByIdentity(
        nextMessages,
        cursor.currentMessageId,
        {
          type: "validation",
          id: chunk.id,
          state: "done",
          pass: chunk.pass === true,
          validationType: chunk.validationType,
          attempt: chunk.attempt,
          maxRetries: chunk.maxRetries,
          feedback: chunk.feedback,
        },
        (p) =>
          p &&
          p.type === "validation" &&
          p.attempt === chunk.attempt &&
          p.validationType === chunk.validationType,
        owned
      );
      break;
    }

    case "finish": {
      finalizeActiveParts(nextMessages, cursor, TEXT_PART, owned);
      finalizeActiveParts(nextMessages, cursor, REASONING_PART, owned);
      for (const [, pointer] of Object.entries(cursor.activeToolPartByCallId || {})) {
        updatePart(nextMessages, pointer.messageId, pointer.partIndex, (part) => {
          if (part.state === "input-streaming" || part.state === "input-available") {
            return { ...part, state: part.input !== undefined ? "input-available" : "done" };
          }
          return part;
        }, owned);
      }
      for (let i = 0; i < nextMessages.length; i++) {
        if (nextMessages[i].status === "streaming") {
          nextMessages[i] = {
            ...nextMessages[i],
            status: "done",
            updatedAt: nowIso(),
          };
        }
      }
      cursor.currentMessageId = null;
      cursor.activeToolPartByCallId = {};
      cursor.streamedMessageIds = [];
      cursor.finished = true;
      cursor.finishReason =
        typeof chunk.finishReason === "string" && chunk.finishReason.trim()
          ? chunk.finishReason.trim()
          : "stop";
      break;
    }

    default:
      appendError(cursor, "UNKNOWN_CHUNK_TYPE", `unknown chunk type ${chunk.type}`, chunk);
      break;
  }

  return { cursor, messages: nextMessages };
}

export function getUiChunkDebugSummary(cursor, messages) {
  if (!cursor) {
    return {
      chunkCount: 0,
      messageCount: 0,
      errorCount: 0,
      lastChunkType: null,
      finished: false,
      finishReason: null,
    };
  }

  return {
    chunkCount: Number(cursor.chunkCount || 0),
    messageCount: Array.isArray(messages) ? messages.length : 0,
    errorCount: Array.isArray(cursor.errors) ? cursor.errors.length : 0,
    lastChunkType: cursor.lastChunkType || null,
    finished: Boolean(cursor.finished),
    finishReason: cursor.finishReason || null,
  };
}
